<template>
	<div
		:class="{ 'customer-container': true, development: isUserInDev }"
		ref="container"
	>
		<ModalManager></ModalManager>
		<LoaderComponent v-if="userStore.isInitialLoading" />
		<router-view
			v-else
			:fimenu="fimenu"
		/>
		<notifications
			group="customerApp"
			dangerouslySetInnerHtml
		/>
	</div>
</template>

<script setup lang="ts">
	import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue';
	import { useRoute, useRouter } from 'vue-router';
	import { broadcastEmitEvent } from '@core/helpers/broadcast-helper';
	import { Emitter } from 'mitt';
	import ENUMS from '@core/classes/Enums';
	import { EventBusCore } from '@core/services/util';
	import FIMenuCustomerViewed from '@core/classes/FIMenuCustomerViewed';
	import FIMenuForCustomerMeeting from '@core/classes/FIMenuForCustomerMeeting';
	import LoaderComponent from '@core/components/UI/LoaderComponent.vue';
	import ModalManager from '@core/components/ModalManager.vue';
	import { notify } from '@kyvg/vue3-notification';
	import settings from 'settings';
	import { useFIMenuForCustomerMeetingStore } from '@core/stores/FIMenuForCustomerMeetingStore';
	import { useUserStore } from '@core/stores/UserStore';
	import util from '@core/services/util';

	const $meetingHub = inject<any>('$meetingHub', null);
	const meetingHelper = inject<any>('meetingHelper', null);
	const MeetingHubBus = inject<Emitter<any>>('MeetingHubBus', null);
	const EventBus = inject<Emitter<any>>('EventBus', null);
	const $route = useRoute();
	const $router = useRouter();

	const userStore = useUserStore();
	const fimenuStore = useFIMenuForCustomerMeetingStore();
	fimenuStore.setFIMenu(null);
	const fimenu = computed(() => fimenuStore.fimenu);

	const isUserInDev = computed(() => {
		return settings.environmentName === 'DEVELOPMENT';
	});
	onMounted(async () => {
		init();

		setInterval(() => {
			// Role Customer used to avoid concurrent Notifications sent (both customer and cocustomer)
			if (
				$meetingHub.connected &&
				meetingHelper.currentMeetingDetails &&
				meetingHelper.myUserDetails &&
				meetingHelper.currentMeetingDetails &&
				meetingHelper.myUserDetails
			) {
				if (meetingHelper.myUserDetails?.isUserWaiting && (meetingHelper.myRole == 'customer' || meetingHelper.myRole == 'coCustomer')) {
					broadcastEmitEvent(
						$meetingHub,
						ENUMS.EVENT_EMITS.BROADCAST.CUSTOMER_WAITING_MEETING,
						JSON.stringify({
							currentMeetingDetails: meetingHelper.currentMeetingDetails,
							myUserDetails: meetingHelper.myUserDetails,
						}),
					);
				}
			}
		}, 10000);
	});

	onBeforeUnmount(() => {
		MeetingHubBus.off('languageChanged', languageChangedHandler);
		MeetingHubBus.off('triggerCustomerAction', triggerCustomerActionHandler);
		MeetingHubBus.off('registerCustomerViewed', registerCustomerViewedHandler);
		EventBusCore.off('toastr', toastrEmitterHandler);
	});

	const init = () => {
		window.addEventListener('beforeunload', handleBeforeClosePage);
		// window.addEventListener('unload', handleClosePage)

		MeetingHubBus.on('languageChanged', languageChangedHandler);
		MeetingHubBus.on('triggerCustomerAction', triggerCustomerActionHandler);
		MeetingHubBus.on('registerCustomerViewed', registerCustomerViewedHandler);
		EventBusCore.on('toastr', toastrEmitterHandler);
	};

	const toastrEmitterHandler = (notifData: any) => {
		const { title, type, duration, text } = notifData;

		notify({
			group: 'customerApp',
			title: title + "<div style='position: absolute; top: 7px; right: 15px;'>x</div>",
			type: type,
			duration: duration,
			text: util.formatToastrText(text),
			closeOnClick: true,
		});
	};
	const redirectTo = (address: string) => {
		if ($route.fullPath != address) {
			$router.replace(address);
		}
	};
	const languageChangedHandler = (language: string) => {
		fimenu.value.language = language;
	};
	const triggerCustomerActionHandler = ({ action, fimenuStringified }: any) => {
		if (!meetingHelper.myUserDetails.isUserWaiting) {
			if (fimenuStringified) {
				const updatedFimenuForCustomerInstance = JSON.parse(fimenuStringified);
				// Object.assign(fimenu, new FIMenuForCustomerMeeting(updatedFIMenu));
				// fimenu.value = new FIMenuForCustomerMeeting(structuredClone<FIMenuForCustomerMeeting>(updatedFimenuForCustomerInstance));
				console.log('updatedFimenuForCustomerInstance', updatedFimenuForCustomerInstance);
				fimenuStore.setFIMenu(updatedFimenuForCustomerInstance);
			}
			switch (action) {
				case ENUMS.CUSTOMER_TRIGGERS.OPEN_VIDEO:
					redirectTo(`/meetingroom/${$route.params.code}/presentation`);
					break;
				case ENUMS.CUSTOMER_TRIGGERS.PAYMENT_SHEET:
					redirectTo(`/meetingroom/${$route.params.code}/coverage-selection`);
					break;
				case ENUMS.CUSTOMER_TRIGGERS.PAPERWORK:
					redirectTo(`/meetingroom/${$route.params.code}/paperwork-signing`);
					break;
				case ENUMS.CUSTOMER_TRIGGERS.INFORMATION:
					redirectTo(`/meetingroom/${$route.params.code}/stand-by?type=information`);
					break;
				case ENUMS.CUSTOMER_TRIGGERS.PAPERWORK_SCREEN:
					redirectTo(`/meetingroom/${$route.params.code}/stand-by?type=paperwork`);
					break;
				case ENUMS.CUSTOMER_TRIGGERS.LOAD_NEXT_DOCUMENT:
					EventBus.emit('triggerLoadNextDocument');
					break;
				case ENUMS.CUSTOMER_TRIGGERS.INVOKE_CLICKED_TO_SIGN:
					EventBus.emit('invokeClickedToSign');
					break;
				case ENUMS.CUSTOMER_TRIGGERS.FIMANAGER_CONNECTED:
					redirectTo(`/meetingroom/${$route.params.code}/stand-by?type=purchase`);
					break;
			}
		}
	};
	const registerCustomerViewedHandler = (customerViewedInfo: FIMenuCustomerViewed) => {
		console.log('Do we have customer viewed', customerViewedInfo);
		fimenu.value.customerViewed.push(new FIMenuCustomerViewed(customerViewedInfo));
	};

	const handleBeforeClosePage = (e?: any) => {
		console.log('HANDLE BEFORE CLOSE PAGE', $route);

		if ($meetingHub.connected) {
			// TODO: THESE TWO METHODS ARE REDUNDANT, SHOULD BE MERGED INTO ONE
			$meetingHub.customerDisconnect();
			broadcastEmitEvent(
				$meetingHub,
				ENUMS.EVENT_EMITS.BROADCAST.CUSTOMER_LEFT_MEETING,
				JSON.stringify({
					meetingDetails: meetingHelper.currentMeetingDetails,
					userDetails: meetingHelper.myUserDetails,
				}),
			);
		}
	};
</script>

<style></style>
